import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  ExternalLink, 
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { getStripeConnectStatus, initStripeConnect, continueStripeOnboarding, getStripeDashboardLink } from '../lib/api';
import { toast } from 'sonner';

export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await getStripeConnectStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load Stripe status:', error);
      setStatus({ status: 'not_connected', connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setActionLoading(true);
    try {
      const response = await initStripeConnect();
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.onboarding_complete) {
        toast.success('Stripe account already connected!');
        loadStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe connection');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    setActionLoading(true);
    try {
      const response = await continueStripeOnboarding();
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to continue onboarding');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDashboard = async () => {
    setActionLoading(true);
    try {
      const response = await getStripeDashboardLink();
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open Stripe dashboard');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const stripeStatus = status?.status || 'not_connected';

  // Not connected
  if (stripeStatus === 'not_connected' || !status?.connected) {
    return (
      <Card className="border-amber-200 bg-amber-50/50" data-testid="stripe-connect-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <CreditCard className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Connect Stripe to Receive Payouts</CardTitle>
              <CardDescription>Set up your payout account to receive earnings from bookings</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <ul className="text-sm text-slate-600 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Secure payments via Stripe
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Direct deposits to your bank
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Track all your earnings
              </li>
            </ul>
            <Button 
              onClick={handleConnect} 
              disabled={actionLoading}
              className="w-full"
              data-testid="connect-stripe-btn"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Connect Stripe
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Pending - onboarding in progress
  if (stripeStatus === 'pending') {
    return (
      <Card className="border-blue-200 bg-blue-50/50" data-testid="stripe-connect-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Stripe Verification in Progress</CardTitle>
              <CardDescription>Complete your Stripe setup to enable payouts</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Your Stripe account is being verified. If you haven't completed all steps, 
              click below to continue the setup process.
            </p>
            {status?.requirements_due > 0 && (
              <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-100 p-2 rounded">
                <AlertCircle className="w-4 h-4" />
                {status.requirements_due} item(s) need your attention
              </div>
            )}
            <Button 
              onClick={handleContinueOnboarding} 
              disabled={actionLoading}
              variant="outline"
              className="w-full"
              data-testid="continue-onboarding-btn"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Continue Setup
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Restricted - has issues
  if (stripeStatus === 'restricted') {
    return (
      <Card className="border-red-200 bg-red-50/50" data-testid="stripe-connect-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Action Required</CardTitle>
              <CardDescription>Your Stripe account needs attention</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Your Stripe account has restrictions. Please visit Stripe to resolve any issues 
              and enable payouts.
            </p>
            {status?.disabled_reason && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-100 p-2 rounded">
                <AlertCircle className="w-4 h-4" />
                {status.disabled_reason.replace(/_/g, ' ')}
              </div>
            )}
            <Button 
              onClick={handleContinueOnboarding} 
              disabled={actionLoading}
              variant="destructive"
              className="w-full"
              data-testid="fix-stripe-btn"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Fix Issues in Stripe
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Verified - all good
  return (
    <Card className="border-green-200 bg-green-50/50" data-testid="stripe-connect-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Stripe Connected — Payouts Enabled</CardTitle>
            <CardDescription>You're all set to receive payments</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-slate-600">Charges enabled</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-slate-600">Payouts enabled</span>
            </div>
          </div>
          <Button 
            onClick={handleViewDashboard} 
            disabled={actionLoading}
            variant="outline"
            className="w-full"
            data-testid="view-stripe-dashboard-btn"
          >
            {actionLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                View Stripe Dashboard
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
